import React, { useState, useEffect } from 'react';

// ── Paleta ──────────────────────────────────────────────────────────────────
const C = {
  navy:         '#0f1c35',
  navyMid:      '#132240',
  navyLt:       '#1a3260',
  cyan:         '#00b8cc',
  cyanDim:      '#0099aa',
  cyanGlow:     'rgba(0,184,204,0.18)',
  white:        '#f0f4f8',
  gray:         '#8a95a3',
  grayLt:       '#bdc5cf',
  border:       'rgba(0,184,204,0.18)',
  borderSub:    'rgba(255,255,255,0.07)',
  surface:      'rgba(255,255,255,0.04)',
  surfaceHover: 'rgba(255,255,255,0.07)',
};

// ── Props ────────────────────────────────────────────────────────────────────
interface TovisHeaderProps {
  onSair:        () => void;
  alertasTotal?: number;
  alertasUrgentes?: number;
  onAlertas?: () => void;
  nomeUsuario?:  string;
  papelUsuario?: string;
  ciclo?:        string;
  se?:           string;
  seIntervalo?:  string;
  fad01Hoje?:    number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const MESES = ['janeiro','fevereiro','março','abril','maio','junho',
               'julho','agosto','setembro','outubro','novembro','dezembro'];

function formatDate(d: Date): string {
  return `${String(d.getDate()).padStart(2,'0')} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

function formatTime(d: Date): string {
  const p = (n: number) => String(n).padStart(2,'0');
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function saudacao(d: Date): string {
  const h = d.getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

// SE brasileira: dom-sáb, ancorando em 04/01/2026 (SE 1)
const SE1_MS = Date.UTC(2026, 0, 4);
function calcularSE(d: Date): number {
  const delta = Math.floor(
    (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - SE1_MS) / 86400000
  );
  return delta < 0 ? 0 : Math.floor(delta / 7) + 1;
}
function formatarIntervalSE(se: number): string {
  const ini = new Date(SE1_MS + (se - 1) * 7 * 86400000);
  const fim = new Date(SE1_MS +  se      * 7 * 86400000 - 86400000);
  const fmt = (d: Date) =>
    `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}`;
  return `${fmt(ini)}–${fmt(fim)}`;
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(' ').filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0][0].toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// ── Hook relógio ─────────────────────────────────────────────────────────────
function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ── Sub-componentes internos ──────────────────────────────────────────────────
function StatusDot({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <style>{`@keyframes sinoPulsa{0%,100%{transform:scale(1)}50%{transform:scale(1.12)}}`}</style>
      
      <span className="tovis-pulse-dot" style={{
        display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
        background: C.cyan, boxShadow: `0 0 6px ${C.cyan}`,
      }} />
      <span style={{
        fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
        fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: C.cyan,
      }}>
        {label}
      </span>
    </div>
  );
}

function AvatarCircle({ nome }: { nome: string }) {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${C.navyLt}, ${C.cyanDim})`,
      border: `1.5px solid ${C.cyan}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, color: C.white,
      fontFamily: "'Inter', sans-serif",
    }}>
      {iniciais(nome)}
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      width: 1, height: 20, background: C.borderSub, flexShrink: 0,
    }} />
  );
}

// ── SVG decorações do hero ─────────────────────────────────────────────────���──
function HeroDecorations() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {/* Grid de linhas */}
      <svg style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 340, opacity: 0.07 }}
           viewBox="0 0 340 120" preserveAspectRatio="none">
        {Array.from({ length: 9 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 42} y1={0} x2={i * 42} y2={120} stroke={C.cyan} strokeWidth={0.8} />
        ))}
        {Array.from({ length: 7 }).map((_, i) => (
          <line key={`h${i}`} x1={0} y1={i * 20} x2={340} y2={i * 20} stroke={C.cyan} strokeWidth={0.8} />
        ))}
      </svg>
      {/* Arco circular */}
      <svg style={{ position: 'absolute', right: -60, top: -60, opacity: 0.12 }}
           width={260} height={260} viewBox="0 0 260 260">
        <circle cx={130} cy={130} r={110} fill="none" stroke={C.cyan} strokeWidth={1.2} />
        <circle cx={130} cy={130} r={80}  fill="none" stroke={C.cyan} strokeWidth={0.6} />
      </svg>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function TovisHeader({
  onSair,
  alertasTotal = 0,
  alertasUrgentes = 0,
  onAlertas,
  nomeUsuario  = 'Supervisor',
  papelUsuario = 'Supervisor',
  ciclo        = 'Ciclo 2/2026',
  se:           seProp,
  seIntervalo:  seIntervaloProp,
  fad01Hoje    = 0,
}: TovisHeaderProps) {
  const now = useClock();
  const seNum      = calcularSE(now);
  const se         = seProp        ?? `SE ${seNum}`;
  const seIntervalo = seIntervaloProp ?? formatarIntervalSE(seNum);

  return (
    <>
      {/* Keyframes via <style> */}
      <style>{`
        @keyframes tovisPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.85); }
        }
        .tovis-pulse-dot { animation: tovisPulse 2s ease-in-out infinite; }
        .tovis-btn-sair:hover { background: ${C.surfaceHover} !important; border-color: rgba(0,184,204,0.4) !important; }
        @media (max-width: 600px) { .tovis-topbar-subtitle { display: none; } }
      `}</style>

      {/* ── TOP BAR ── */}
      <div style={{
        height: 52, background: '#0a1525',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center',
        padding: '0 20px', gap: 14,
        fontFamily: "'Inter', sans-serif",
        position: 'relative', zIndex: 10,
      }}>
        {/* Logo + nome + subtítulo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img
            src="/logo.png" alt="Tovis"
            style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover',
                     background: '#0a1525', border: '1px solid rgba(0,184,204,0.3)', flexShrink: 0 }}
          />
          <span style={{ fontSize: 16, fontWeight: 700, color: '#f0f4f8', lineHeight: 1 }}>
            Tovis
          </span>
          <span className="tovis-topbar-subtitle" style={{
            fontSize: 12, color: '#8a95a3', lineHeight: 1,
          }}>
            Sistema de Gestão de Endemias
          </span>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Badge ciclo */}
        <div style={{
          background: C.cyanGlow, border: `1px solid ${C.border}`,
          borderRadius: 6, padding: '3px 10px',
          fontSize: 11, fontWeight: 600, color: C.cyan, letterSpacing: '0.04em',
        }}>
          {ciclo}
        </div>

        <Divider />

        {/* Relógio */}
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 13, fontWeight: 500, color: C.gray,
          letterSpacing: '0.04em',
        }}>
          {formatTime(now)}
        </span>

        <Divider />

        {onAlertas && (
          <button onClick={onAlertas} title="Ver alertas" style={{
            position: 'relative', background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
            width: 40, height: 40, cursor: 'pointer', fontSize: 18, marginRight: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: alertasUrgentes > 0 ? 'sinoPulsa 1.4s ease-in-out infinite' : 'none' }}>
            🔔
            {alertasTotal > 0 && (
              <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 18, height: 18,
                padding: '0 5px', borderRadius: 9, fontSize: 11, fontWeight: 800, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: alertasUrgentes > 0 ? '#e0242b' : '#00b8cc',
                boxShadow: alertasUrgentes > 0 ? '0 0 0 3px rgba(224,36,43,.3)' : 'none' }}>{alertasTotal}</span>
            )}
          </button>
        )}
        {/* Avatar + nome + papel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AvatarCircle nome={nomeUsuario} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.white }}>{nomeUsuario}</span>
            <span style={{ fontSize: 10, color: C.gray, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{papelUsuario}</span>
          </div>
        </div>

        <Divider />

        {/* Botão sair */}
        <button
          className="tovis-btn-sair"
          onClick={onSair}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: C.surface, border: `1px solid ${C.borderSub}`,
            borderRadius: 7, padding: '5px 12px', cursor: 'pointer',
            fontSize: 12, fontWeight: 500, color: C.grayLt,
            fontFamily: "'Inter', sans-serif",
            transition: 'background 0.2s, border-color 0.2s',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sair
        </button>
      </div>

      {/* ── HERO ── */}
      <div style={{
        background: `linear-gradient(120deg, #0a1525 0%, ${C.navyMid} 55%, #0d2340 100%)`,
        paddingTop: 36, paddingBottom: 32, paddingLeft: 28, paddingRight: 28,
        position: 'relative', overflow: 'hidden',
        fontFamily: "'Inter', sans-serif",
      }}>
        <HeroDecorations />

        <div style={{
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
        }}>
          {/* ESQUERDA */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
            {/* Logo grande */}
            <div style={{
              width: 72, height: 72, borderRadius: 18, flexShrink: 0,
              background: `linear-gradient(135deg, ${C.navyLt} 0%, ${C.cyanDim} 100%)`,
              boxShadow: `0 0 28px ${C.cyanGlow}, 0 4px 16px rgba(0,0,0,0.4)`,
              border: `1.5px solid ${C.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Tovis" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 18 }} />
            </div>

            {/* Textos */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  fontSize: 32, fontWeight: 800, color: C.white,
                  letterSpacing: '-0.8px', lineHeight: 1,
                }}>
                  Tovis
                </span>
                <StatusDot label="Tempo Real" />
              </div>
              <span style={{ fontSize: 13, color: C.grayLt, fontWeight: 400, letterSpacing: '0.02em' }}>
                Sistema de Gestão de Endemias
              </span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, color: C.gray, letterSpacing: '0.04em',
              }}>
                {saudacao(now)}, {papelUsuario} · {formatDate(now)}
              </span>
            </div>
          </div>

          {/* DIREITA — 2 cards */}
          <div style={{ display: 'flex', gap: 12, flexShrink: 0 }}>
            {/* Card SE */}
            <div style={{
              background: C.cyanGlow, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: '14px 20px', minWidth: 120,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 500, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: C.cyan,
              }}>
                SE Atual
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: C.white, lineHeight: 1 }}>
                {se}
              </span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, color: C.gray,
              }}>
                {seIntervalo}
              </span>
            </div>

            {/* Card Ciclo */}
            <div style={{
              background: C.surface, border: `1px solid ${C.borderSub}`,
              borderRadius: 12, padding: '14px 20px', minWidth: 120,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 500, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: C.gray,
              }}>
                Ciclo
              </span>
              <span style={{ fontSize: 16, fontWeight: 700, color: C.white, lineHeight: 1 }}>
                {ciclo}
              </span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, color: C.gray,
              }}>
                Ativo
              </span>
            </div>

            {/* Card FAD-01 HOJE */}
            <div style={{
              background: C.cyanGlow, border: `1px solid ${C.border}`,
              borderRadius: 12, padding: '14px 20px', minWidth: 120,
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 500, letterSpacing: '0.12em',
                textTransform: 'uppercase', color: C.cyan,
              }}>
                FAD-01 Hoje
              </span>
              <span style={{ fontSize: 22, fontWeight: 700, color: C.white, lineHeight: 1 }}>
                {fad01Hoje}
              </span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, color: C.gray,
              }}>
                {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
